import { createSingleLinePlugin } from '@udecode/plate-break';
import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import { FieldConnector } from 'components/atoms/Form/FieldConnector';
import FreeTextInput from 'components/atoms/Form/FreeTextInput';
import TagsInput from 'components/molecules/Fields/TagsInput/TagsInput';
import type { FC, ReactNode } from 'react';
import { useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { DropdownIndicatorProps } from 'react-select';
import { components } from 'react-select';
import { getTracingEvents } from 'utils/amplitude';
import type { EditorRef, TextEditorProps, TextEditorType } from '../TextEditor';
import TextEditor from '../TextEditor';
import { createVariablePlugin } from '../TextEditor/TextEditorPlugins';
import CommonTextEditor from './CommonTextEditor.component';
import * as amplEvents from './sendEmailAmplitude';
import { tagsInputStyleProps } from './utils';

const eventsDataFn = (tracingData?: Record<string, string>) => ({
  ...tracingData,
});

const events = getTracingEvents({
  onAddTo: {
    name: amplEvents.WEBAPP_SENDEMAIL_ADDTOEMAIL,
    data: eventsDataFn,
  },
  onDeleteTo: {
    name: amplEvents.WEBAPP_SENDEMAIL_DELETETOEMAIL,
    data: eventsDataFn,
  },
  onAddCc: {
    name: amplEvents.WEBAPP_SENDEMAIL_ADDCCEMAIL,
    data: eventsDataFn,
  },
  onDeleteCc: {
    name: amplEvents.WEBAPP_SENDEMAIL_DELETECCEMAIL,
    data: eventsDataFn,
  },
  onAddBcc: {
    name: amplEvents.WEBAPP_SENDEMAIL_ADDBCCEMAIL,
    data: eventsDataFn,
  },
  onDeleteBcc: {
    name: amplEvents.WEBAPP_SENDEMAIL_DELETEBCCEMAIL,
    data: eventsDataFn,
  },
  onClickSubject: {
    name: amplEvents.WEBAPP_SENDEMAIL_CLICKEDSUBJECT,
    data: eventsDataFn,
  },
});

type CommonEmailFieldsProps = {
  renderAttachments?: (editor: TextEditorType) => ReactNode;
  renderExtraEditorControls?: (editor: TextEditorType) => ReactNode;
  renderExtraHeaderContent?: () => ReactNode;
  renderCcFieldWarning?: () => ReactNode;
  renderSubjectControls?: () => ReactNode;
  editorRef?: EditorRef;
  subjectRef?: EditorRef;
  tracingData?: Record<string, string>;
  editorDefaultValue?: TextEditorProps['initialValue'];
  editorOnBlur?: TextEditorProps['onBlur'];
  subjectOnBlur?: TextEditorProps['onBlur'];
  editorOnChange?: TextEditorProps['onChange'];
  subjectOnChange?: TextEditorProps['onChange'];
  variablesData?: Record<string, any>;
};

export type CommonFieldsType = {
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  to_draft: string;
  cc_draft: string;
  bcc_draft: string;
};

const CommonEmailFields: FC<CommonEmailFieldsProps> = ({
  renderAttachments,
  renderExtraEditorControls,
  renderCcFieldWarning,
  renderExtraHeaderContent,
  renderSubjectControls,
  editorRef,
  subjectRef,
  tracingData,
  editorOnBlur,
  subjectOnBlur,
  editorOnChange,
  subjectOnChange,
  variablesData,
}) => {
  const { t } = useTranslation();
  const formAPI = useFormContext<CommonFieldsType>();

  const [shouldShowBcc, setShouldShowBcc] = useState(
    () => !!formAPI.getValues('bcc')?.length,
  );
  const [shouldShowCc, setShouldShowCc] = useState(
    () => !!formAPI.getValues('cc')?.length,
  );

  const DropdownIndicator = useCallback(
    (props: DropdownIndicatorProps) => {
      if (shouldShowBcc && shouldShowCc) return null;
      return (
        <components.DropdownIndicator {...props}>
          <div className="flex gap-2">
            {!shouldShowCc && (
              <Button
                asDefault
                className="text-primary-400 text-sm hover:underline focus:underline"
                onClick={() => setShouldShowCc(true)}
              >
                {t('email_client.field.cc.label')}
              </Button>
            )}
            {!shouldShowBcc && (
              <Button
                asDefault
                className="text-primary-400 text-sm hover:underline focus:underline"
                onClick={() => setShouldShowBcc(true)}
              >
                {t('email_client.field.bcc.label')}
              </Button>
            )}
          </div>
        </components.DropdownIndicator>
      );
    },
    [shouldShowBcc, shouldShowCc],
  );

  const [to_draft = '', cc_draft = '', bcc_draft = ''] = formAPI.watch([
    'to_draft',
    'cc_draft',
    'bcc_draft',
  ]);

  type getTagsChangePropsType = {
    draftKey: 'to_draft' | 'cc_draft' | 'bcc_draft';
    draftValue: string;
    fieldKey: 'to' | 'cc' | 'bcc';
    onAfterAdd?: () => void;
    onAfterDelete?: () => void;
  };
  const getTagsChangeProps = (data: getTagsChangePropsType) => ({
    inputValue: data.draftValue,
    resetInputOnAdd: false,
    onInputChange: (str: string) =>
      formAPI.setValue(data.draftKey, str, {
        shouldValidate: !str,
      }),
    onChange: (tags: string[], action?: string) => {
      if (action === 'remove-value') {
        data.onAfterDelete?.();
        return formAPI.setValue(data.fieldKey, tags, { shouldValidate: true });
      }
      formAPI.trigger(data.draftKey).then((isValid) => {
        if (isValid) {
          data.onAfterAdd?.();
          formAPI.setValue(data.fieldKey, tags, { shouldValidate: true });
          formAPI.setValue(data.draftKey, '', { shouldValidate: true });
        }
      });
    },
  });

  const innerEditorRef = useRef<TextEditorType>(undefined);
  useImperativeHandle(editorRef, () => innerEditorRef.current);

  const innerSubjectRef = useRef<TextEditorType>(undefined);
  useImperativeHandle(subjectRef, () => innerSubjectRef.current);

  return (
    <div className="border border-primary-50 rounded-xl p-1 bg-primary-25">
      <div className="border border-primary-100 bg-white rounded-lg min-h-[600px] flex flex-col">
        <div className="p-4 bg-primary-25 flex flex-col gap-2 rounded-t-lg">
          {renderExtraHeaderContent?.()}
          <FieldConnector
            horizontal
            labelClassName="w-[75px] text-primary-400"
            name="to"
            errorKeys={['to_draft', 'to']}
            label={t('email_client.field.to.label')}
          >
            {({ field, error }) => {
              return (
                <TagsInput
                  {...field}
                  isError={!!error}
                  {...getTagsChangeProps({
                    draftKey: 'to_draft',
                    draftValue: to_draft,
                    fieldKey: 'to',
                    onAfterAdd: () => events.onAddTo(tracingData),
                    onAfterDelete: () => events.onDeleteTo(tracingData),
                  })}
                  components={{
                    ...tagsInputStyleProps,
                    DropdownIndicator,
                  }}
                />
              );
            }}
          </FieldConnector>
          <FieldConnector
            showField={shouldShowCc}
            destroyValue={[]}
            horizontal
            labelClassName="w-[75px] text-primary-400"
            name="cc"
            errorKeys={['cc_draft', 'cc']}
            label={t('email_client.field.cc.label')}
          >
            {({ field, error }) => (
              <TagsInput
                {...field}
                isError={!!error}
                {...getTagsChangeProps({
                  draftKey: 'cc_draft',
                  draftValue: cc_draft,
                  fieldKey: 'cc',
                  onAfterAdd: () => events.onAddCc(tracingData),
                  onAfterDelete: () => events.onDeleteCc(tracingData),
                })}
                components={tagsInputStyleProps}
              />
            )}
          </FieldConnector>
          {renderCcFieldWarning?.()}
          <FieldConnector
            showField={shouldShowBcc}
            destroyValue={[]}
            horizontal
            labelClassName="w-[75px] text-primary-400"
            name="bcc"
            errorKeys={['bcc_draft', 'bcc']}
            label={t('email_client.field.bcc.label')}
          >
            {({ field, error }) => (
              <TagsInput
                {...field}
                isError={!!error}
                {...getTagsChangeProps({
                  draftKey: 'bcc_draft',
                  draftValue: bcc_draft,
                  fieldKey: 'bcc',
                  onAfterAdd: () => events.onAddBcc(tracingData),
                  onAfterDelete: () => events.onDeleteBcc(tracingData),
                })}
                components={tagsInputStyleProps}
              />
            )}
          </FieldConnector>
          <FieldConnector
            horizontal
            labelClassName="w-[75px] text-primary-400"
            name="subject"
            label={t('email_client.field.subject.label')}
          >
            {({ field }) => (
              <FreeTextInput
                {...field}
                // showErrorIcon={false}
                // className="text-sm !h-auto !py-3 leading-tight"
                renderInput={({ className, onBlur, onFocus }) => (
                  <div
                    className={classNames(className, 'flex items-center gap-2')}
                  >
                    <div className="flex-1">
                      <TextEditor
                        editorRef={innerSubjectRef}
                        plugins={[
                          createSingleLinePlugin(),
                          createVariablePlugin(),
                        ]}
                        replaceDefaultPlugins
                        renderFooter={() => null}
                        onFocus={() => {
                          onFocus();
                          events.onClickSubject(tracingData);
                        }}
                        onBlur={() => {
                          onBlur();
                          subjectOnBlur?.();
                        }}
                        onChange={subjectOnChange}
                      />
                    </div>
                    {renderSubjectControls?.()}
                  </div>
                )}
              />
            )}
          </FieldConnector>
        </div>
        <CommonTextEditor
          aiHelper
          variablesData={variablesData}
          editorRef={innerEditorRef}
          renderAttachments={renderAttachments}
          renderExtraEditorControls={renderExtraEditorControls}
          onBlur={editorOnBlur}
          onChange={editorOnChange}
          tracing={{
            onFocus: {
              name: amplEvents.WEBAPP_SENDEMAIL_CLICKEDEMAILCONTENT,
              data: tracingData,
            },
            onToggleBold: {
              name: amplEvents.WEBAPP_SENDEMAIL_TOGGLEBOLD,
              data: tracingData,
            },
            onToggleItalic: {
              name: amplEvents.WEBAPP_SENDEMAIL_TOGGLEITALIC,
              data: tracingData,
            },
            onToggleUnderline: {
              name: amplEvents.WEBAPP_SENDEMAIL_TOGGLEUNDERLINE,
              data: tracingData,
            },
          }}
        />
      </div>
    </div>
  );
};

export default CommonEmailFields;
